(function () {
  /**
   * Checkout orchestrator — intercepts the cart's checkout button (was a
   * submit to Shopify's hosted checkout) and opens a single bottom-sheet
   * instead, matching checkout_modal.dart / checkout_modal_sheet.dart: one
   * sheet with delivery + payment + summary + confirm, not a multi-step
   * wizard, and no dedicated checkout URL.
   */

  var STR = (window.ErosAccountStrings && window.ErosAccountStrings.checkout) || {};
  function t(key, fallback) {
    return STR[key] || fallback;
  }

  // Documented fallback for GET /v1/checkout/shipping-fees on any fetch
  // failure — matches the 3000/6000 IQD commerce-worker's own endpoint
  // falls back to (last-known-good cache, else this same hardcoded value —
  // shared contract) and the Flutter app's equivalent fallback. Titles
  // match the two live Shopify delivery method names the owner controls
  // himself in Admin ("تغليف قياسي" / "تغليف كهدية", zone "Domestic") so
  // the summary row reads identically whether the live fetch succeeded or
  // not — only the price differs when the owner changes it in Shopify.
  var FALLBACK_SHIPPING_FEES = {
    delivery: { title: 'كلفة التوصيل و التغليف', price: 3000, currency: 'IQD' },
    gift: { title: 'تغليف كهدية', price: 6000, currency: 'IQD' },
  };

  var confirmEventId = null;
  var confirmEventKey = null;
  function generateEventId() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'eros-' + Date.now() + '-' + Math.random().toString(36).slice(2);
  }

  // initialMode: 'delivery' (default) | 'gift' — which fulfillment the sheet
  // opens preset to. The cart swipe bar passes it (swipe/click right =
  // delivery, left = gift); the plain button path passes nothing. Store
  // pickup is closed (ADR-243).
  function openCheckout(initialMode) {
    // ADR-202: in an order-add session the cart sends to that order instead.
    if (window.ErosOrderAdd && window.ErosOrderAdd.active()) {
      window.ErosOrderAdd.submit();
      return;
    }
    if (!window.ErosSession.isSignedIn()) {
      openAuthGate(initialMode);
      return;
    }
    proceedToCheckout(initialMode);
  }

  function openAuthGate(initialMode) {
    var panel = window.ErosUI.el('div', '', { 'data-eros-auth-panel': '' });
    var unsubscribe = null;
    var sheet = window.ErosUI.openSheet({
      title: t('sign_in_to_continue', 'سجّل الدخول لإتمام الطلب'),
      content: panel,
      // Closing the gate without signing in used to leave this listener
      // alive: a later sign-in anywhere on the page (or in another tab —
      // the storage event) reopened the checkout sheet out of nowhere
      // (audit 2026-09-13).
      onClose: function () {
        if (unsubscribe) unsubscribe();
        unsubscribe = null;
      },
    });
    if (window.ErosAccountAuth) window.ErosAccountAuth.mountAll();

    unsubscribe = window.ErosSession.onChange(function (session) {
      if (session && session.customerGid) {
        if (unsubscribe) unsubscribe();
        unsubscribe = null;
        sheet.close();
        proceedToCheckout(initialMode);
      }
    });
  }

  function proceedToCheckout(initialMode) {
    fetch('/cart.js')
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        if (!cart.items || !cart.items.length) {
          window.ErosUI.toast(t('empty_cart', 'السلة فاضية'), 'error');
          return;
        }
        // Mirrors Flutter's refreshCartLineAvailability: reconcile the cart
        // against reservation-aware stock BEFORE the sheet renders its totals,
        // so the customer sees the real quantities here instead of being
        // refused at the pay button a moment later.
        return reconcileCartWithHolds(cart).then(function (result) {
          // reconcileCartWithHolds returns either the cart itself, or
          // { cart, soldOut } when something in it went out of stock.
          var fresh = result && result.cart ? result.cart : result;
          var soldOut = (result && result.soldOut) || [];

          if (soldOut.length) {
            var names = soldOut
              .map(function (e) {
                return e.title;
              })
              .filter(Boolean)
              .join('، ');
            window.ErosUI.toast(
              t(
                'sold_out_remove',
                'نفدت الكمية — احذفه من سلتك ثم تابع:',
              ) + (names ? ' ' + names : ''),
              'error',
            );
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            return;
          }

          if (!fresh.items || !fresh.items.length) {
            window.ErosUI.toast(t('empty_cart', 'السلة فاضية'), 'error');
            return;
          }
          // ADR-200: a cart holding «بطاقة رصيد إيروس» — cards only, or
          // cards with products (owner 2026-10-02) — pays online only.
          return giftCreditCartKind(fresh).then(function (gift) {
            if (gift.kind !== 'none' && !gift.config.enabled) {
              window.ErosUI.toast(window.ErosGiftCredit.checkoutErrorMessage('gift_credit_disabled'), 'error');
              return;
            }
            openCheckoutSheet(fresh, initialMode, gift.kind !== 'none' ? gift : null);
          });
        });
      })
      .catch(function () {
        window.ErosUI.toast(t('generic_error', 'حدث خطأ، حاول مرة أخرى'), 'error');
      });
  }

  /** ADR-200: { kind: 'none' | 'gift' | 'mixed', config } for this cart (eros-gift-credit.js). */
  function giftCreditCartKind(cart) {
    if (!window.ErosGiftCredit) return Promise.resolve({ kind: 'none', config: null });
    return window.ErosGiftCredit.config().then(function (cfg) {
      var ids = (cart.items || []).map(function (item) {
        return item.variant_id;
      });
      return { kind: window.ErosGiftCredit.cartKind(cfg, ids), config: cfg };
    });
  }

  /**
   * Clamps cart lines to what is actually buyable once other customers' live
   * card holds are subtracted, exactly as the app does. Resolves with the cart
   * to use — the ORIGINAL cart on any failure, since a hiccup here must never
   * shrink someone's cart or block checkout (the server-side gate at confirm
   * is the real boundary; this is display sharpness).
   */
  /**
   * Flags the sold-out lines in the cart UI (drawer + /cart) so the customer
   * can see WHICH product to remove. Purely additive: it never removes a line
   * or changes a quantity — that decision is the customer's.
   *
   * Matches on the Shopify line key, which both the drawer and the cart page
   * already stamp on each row, and is a no-op wherever those rows are not
   * currently rendered.
   */
  function markSoldOutLines(soldOut) {
    document
      .querySelectorAll('[data-eros-sold-out="1"]')
      .forEach(function (el) {
        el.removeAttribute('data-eros-sold-out');
      });
    if (!soldOut || !soldOut.length) return;

    soldOut.forEach(function (entry) {
      var sel =
        '[data-line-key="' +
        entry.key +
        '"], [data-key="' +
        entry.key +
        '"], [data-cart-line-key="' +
        entry.key +
        '"]';
      document.querySelectorAll(sel).forEach(function (row) {
        row.setAttribute('data-eros-sold-out', '1');
        // The CSS badge renders this via content: attr(), so the label stays
        // translatable instead of being frozen into the stylesheet.
        row.setAttribute(
          'data-eros-sold-out-label',
          t('sold_out_badge', 'نفد'),
        );
      });
    });
  }

  function reconcileCartWithHolds(cart) {
    var byGid = {};
    cart.items.forEach(function (item) {
      if (item.variant_id) byGid['gid://shopify/ProductVariant/' + item.variant_id] = true;
    });
    var gids = Object.keys(byGid);
    if (!gids.length) return Promise.resolve(cart);

    return window.ErosCommerce.variantAvailability(gids)
      .then(function (res) {
        var units = (res && res.units) || {};
        var changes = [];
        var shrunk = [];
        var soldOut = [];
        cart.items.forEach(function (item) {
          var cap = units['gid://shopify/ProductVariant/' + item.variant_id];
          // Absent = no real ceiling (untracked / oversell allowed) — leave it.
          if (typeof cap !== 'number' || item.quantity <= cap) return;
          // cap 0 = sold out. Setting a line to quantity 0 DELETES it from the
          // Shopify cart, which is what silently emptied a cart and left the
          // customer staring at "سلتك فارغة" with no reason (owner report
          // 2026-09-07). Keep the line, name it, and let them remove it.
          if (cap <= 0) {
            soldOut.push({
              key: item.key,
              title: item.product_title || item.title,
            });
            return;
          }
          changes.push({ id: item.key, quantity: cap });
          shrunk.push(item.product_title || item.title);
        });

        markSoldOutLines(soldOut);
        if (!changes.length) {
          return soldOut.length ? { cart: cart, soldOut: soldOut } : cart;
        }

        // Sequential, not parallel: each /cart/change.js returns the whole
        // rebuilt cart, and concurrent writes to it race each other.
        var chain = Promise.resolve(cart);
        changes.forEach(function (change) {
          chain = chain.then(function () {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(change),
            }).then(function (r) {
              return r.json();
            });
          });
        });

        return chain.then(function (updated) {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          window.ErosUI.toast(
            t('out_of_stock_items', 'نفدت الكمية من:') + ' ' + shrunk.join('، '),
            'error',
          );
          markSoldOutLines(soldOut);
          return soldOut.length ? { cart: updated, soldOut: soldOut } : updated;
        });
      })
      .catch(function () {
        return cart;
      });
  }

  // Meta InitiateCheckout for the web (2026-09-14). The custom checkout never
  // enters Shopify's checkout, so the Facebook & Instagram app's pixel — which
  // still owns PageView/ViewContent/AddToCart here — never sees
  // `checkout_started`: InitiateCheckout fell from 30–50/day to ~0 the day
  // the theme launched. The worker forwards this to Meta CAPI (web is allowed
  // for this one event only, meta_capi_mid_funnel.ts). Once per cart token
  // per tab: reopening the sheet after fixing an address is the same intent.
  function readCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }
  // ADR-225: the influencer link this browser arrived by — {ref, at}, or null (then nothing is sent).
  function currentAttribution() {
    return window.ErosAttribution ? window.ErosAttribution.current() : null;
  }
  function trackInitiateCheckout(cart) {
    try {
      var key = 'eros_ic_' + (cart.token || 'nocart');
      if (window.sessionStorage.getItem(key)) return;
      window.sessionStorage.setItem(key, '1');
    } catch (e) { /* storage blocked — still send once for this call */ }
    try {
      var session = window.ErosSession && window.ErosSession.get ? window.ErosSession.get() : null;
      var customerGid = session && session.customerGid ? String(session.customerGid) : '';
      var body = {
        event: 'initiate_checkout',
        platform: 'web',
        eventId: generateEventId(),
        currency: cart.currency || 'IQD',
        value: (cart.total_price || 0) / 100,
        cartLineCount: cart.item_count || (cart.items ? cart.items.length : 0),
        pageLocation: window.location.href,
        fbp: readCookie('_fbp'),
        fbc: readCookie('_fbc'),
      };
      if (customerGid) body.customerId = customerGid.replace(/^gid:\/\/shopify\/Customer\//, '');
      fetch('https://app-api.erosstore.co/api/v1/analytics/track', {
        method: 'POST',
        keepalive: true,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }).catch(function () { /* analytics never blocks checkout */ });
    } catch (e) { /* ignore */ }
  }

  // gift (ADR-200): { kind: 'gift' | 'mixed', config } for a cart holding
  // «بطاقة رصيد إيروس», else null. Any such cart shows only the payment
  // chips the server config allows (no cash on delivery) with a note, and
  // takes no discount code, birthday discount or wallet credit.
  // - 'gift' (cards only): no address or delivery step and no delivery fee
  //   (sent as 'pickup' with the customer's own phone and name).
  // - 'mixed' (cards + products): the normal delivery step and fee.
  function openCheckoutSheet(cart, initialMode, gift) {
    trackInitiateCheckout(cart);
    var giftConfig = gift ? gift.config : null;
    var hasCards = !!gift;
    var giftCredit = hasCards && gift.kind === 'gift'; // cards only
    var isPickup = null; // forces the first setFulfillment(false) call below to actually run its setup
    // ADR-331 W0 (2026-11-03): the sheet starts from the catalogue price and
    // takes every discount from the worker's quote, never from Shopify's cart.
    // cart.total_price carries Shopify automatic discounts (the old BXGY
    // «عرض لوفة») that the order never gets: REST orders/create applies none,
    // so the sheet promised less than the customer was charged. The quote
    // prices the cart code, the influencer link and the birthday code exactly
    // as confirm does; the personal offer keeps its own row (ADR-213).
    var merchandiseTotal = (typeof cart.original_total_price === 'number' ? cart.original_total_price : cart.total_price) / 100;
    var appliedDiscountCode = null;
    // ADR-213: the personal offer's token (stored by eros-offer.js, unexpired
    // only). Gift cards are never discounted, so a card cart carries none.
    var offerToken = !hasCards && window.ErosOffer ? window.ErosOffer.activeToken() : null;
    if (!hasCards && Array.isArray(cart.discount_codes)) {
      for (var di = 0; di < cart.discount_codes.length; di++) {
        if (cart.discount_codes[di] && cart.discount_codes[di].applicable) {
          appliedDiscountCode = cart.discount_codes[di].code;
          break;
        }
      }
    }
    // Left fulfillment slot, mirroring the app's CartSummaryBar: gift when the
    // theme's gift_send mirror flag is on, else none (delivery-only). When
    // 'none' the alt chip is not rendered at all — matching the
    // single-direction delivery swipe on the cart bar.
    var leftMode = giftCredit ? 'none' : fulfillmentLeftMode();
    var hasAlt = leftMode !== 'none';

    // Seeded with the fallback so the very first paint (before the live
    // fetch below resolves) already shows a correct-looking total instead
    // of "undefined" — replaced in place the moment the fetch (or its
    // failure) settles, see the .then()/.catch() near the bottom of this
    // function.
    var shippingFees = FALLBACK_SHIPPING_FEES;
    var isGift = false;

    var body = window.ErosUI.el('div', 'eros-checkout-body');

    // Single fulfillment toggle — mirrors the app's one delivery/gift swipe
    // control on the cart CTA (there it's a swipe gesture; a tap toggle is
    // the web equivalent).
    var fulfillmentHeading = window.ErosUI.el('p', 'h6');
    fulfillmentHeading.textContent = t('fulfillment_heading', 'طريقة الاستلام');
    var fulfillmentChips = window.ErosUI.el('div', 'eros-payment-chips');
    var deliveryChip = window.ErosUI.el('button', 'eros-payment-chip', { type: 'button' });
    deliveryChip.textContent = t('delivery_heading', 'التوصيل');
    var altChip = window.ErosUI.el('button', 'eros-payment-chip', { type: 'button' });
    altChip.textContent = t('gift_toggle_gift', 'إرسال كهدية');
    fulfillmentChips.appendChild(deliveryChip);
    if (hasAlt) fulfillmentChips.appendChild(altChip);
    // With the gift flag off there is no choice to make — hide the whole toggle
    // (heading + lone chip) and go straight to the delivery form, matching the
    // app's delivery-only cart (no fulfillment control on the checkout sheet).
    if (hasAlt) {
      body.appendChild(fulfillmentHeading);
      body.appendChild(fulfillmentChips);
    }

    var deliveryHost = window.ErosUI.el('div');
    body.appendChild(deliveryHost);

    if (hasCards) {
      var giftNote = window.ErosUI.el('p', 'eros-checkout-gift-note');
      giftNote.textContent = window.ErosGiftCredit.t('checkout_note', 'بطاقة الرصيد تُدفع بالبطاقة أو الحوالة');
      body.appendChild(giftNote);
    }

    var paymentHeading = window.ErosUI.el('p', 'h6');
    paymentHeading.textContent = t('payment_heading', 'طريقة الدفع');
    var paymentHost = window.ErosUI.el('div');
    body.appendChild(paymentHeading);
    body.appendChild(paymentHost);

    // Order: Products / Delivery cost / Total, then a distinct wallet-due
    // block below it — matches CheckoutOrderSummary followed by
    // CheckoutWalletDueSection (not merged into the payment section).
    var summary = window.ErosUI.el('div', 'eros-checkout-summary');
    // Small gift-mode cue in the order summary — reuses the theme's own
    // .badge/.badge--custom chip (theme.css), the same convention as the
    // discount badge in cart-drawer.liquid/main-cart.liquid, rather than
    // inventing new visual chrome for this one banner.
    var giftBanner = window.ErosUI.el('span', 'badge badge--custom');
    giftBanner.textContent = t('gift_banner', 'طلب هدية');
    giftBanner.style.display = 'none';
    var subtotalRow = window.ErosUI.el('p');
    // Quote discount row (ADR-170, ADR-331 W0): the worker prices the order's
    // one code (birthday, cart code or influencer link); the sheet must show
    // the same total the customer will be asked to transfer — a Zain Cash
    // customer paid the app's number, not Shopify's, on 2026-09-12.
    var birthdayRow = window.ErosUI.el('p', 'eros-checkout-summary__birthday');
    birthdayRow.style.display = 'none';
    var quoteDiscountIqd = 0;
    var quoteDiscountKind = null;
    var promoQuote = null;
    var promoIqd = 0; // the part of quoteDiscountIqd the promotion rows already show
    var shippingRow = window.ErosUI.el('p');
    var totalRow = window.ErosUI.el('p', 'bold');
    summary.appendChild(giftBanner);
    summary.appendChild(subtotalRow);
    // ADR-213: same «خصم العرض» line as the cart (eros-offer-render.js).
    var offer = window.ErosOfferRender ? window.ErosOfferRender.checkoutRow(function () { refreshSummary(); mountPayment(); }) : { row: document.createTextNode(''), discountIqd: 0 };
    summary.appendChild(offer.row);
    summary.appendChild(birthdayRow);
    summary.appendChild(shippingRow);
    summary.appendChild(totalRow);
    body.appendChild(summary);

    var walletHost = window.ErosUI.el('div');
    body.appendChild(walletHost);

    var totalIqd = merchandiseTotal;
    // Whichever of shippingFees.delivery/.gift is currently selected — the
    // single source of truth both refreshSummary() and mountPayment() read
    // from, so the order-summary total and the wallet-due preview can never
    // disagree about which fee is in effect.
    function selectedFee() {
      return isGift ? shippingFees.gift : shippingFees.delivery;
    }
    function merchandiseNet() {
      return Math.max(0, merchandiseTotal - quoteDiscountIqd - offer.discountIqd);
    }
    // ADR-224: the offer's delivery part lowers the delivery fee (never the
    // gift rate) — the same reduced line the worker puts on the order.
    function shippingNow() {
      if (isPickup) return 0;
      var price = window.ErosPromoCart ? window.ErosPromoCart.sheetShipping(selectedFee().price, promoQuote, isGift) : selectedFee().price;
      return isGift || !offer.shipping || !window.ErosOffer ? price : window.ErosOffer.discountedShippingIqd(price, offer.shipping);
    }
    function refreshSummary() {
      var fee = selectedFee();
      var shippingIqd = shippingNow();
      totalIqd = Math.round(merchandiseNet() + shippingIqd);
      birthdayRow.style.display = quoteDiscountIqd - promoIqd > 0 ? '' : 'none';
      var discountLabel = quoteDiscountKind === 'birthday' ? t('birthday_discount', 'خصم عيد ميلادك 🎂') : t('code_discount', 'الخصم');
      birthdayRow.textContent = discountLabel + ': −' + window.ErosUI.formatMoneyIqd(quoteDiscountIqd - promoIqd);
      // Pickup collapses to a single Total row — matches checkout_order_summary.dart's isPickup branch.
      subtotalRow.style.display = isPickup ? 'none' : '';
      shippingRow.style.display = isPickup ? 'none' : '';
      subtotalRow.textContent = t('subtotal', 'قيمة المنتجات') + ': ' + window.ErosUI.formatMoneyIqd(merchandiseTotal);
      // Live title from GET /v1/checkout/shipping-fees (or the fallback)
      // rather than the generic "shipping" label — shows exactly which of
      // the two Shopify delivery methods ("تغليف قياسي"/"تغليف كهدية") is
      // in effect, per the shared contract's display requirement.
      var shippingLabel = isPickup ? t('shipping', 'كلفة التوصيل') : fee.title;
      shippingRow.textContent = shippingLabel + ': ' + window.ErosUI.formatMoneyIqd(shippingIqd);
      totalRow.textContent = t('total', 'المجموع') + ': ' + window.ErosUI.formatMoneyIqd(totalIqd);
      giftBanner.style.display = !isPickup && isGift ? '' : 'none';
    }

    var error = window.ErosUI.el('p', 'eros-field__error');
    body.appendChild(error);

    var confirmBtn = window.ErosUI.el('button', 'button w-full', { type: 'button' });
    confirmBtn.textContent = t('confirm', 'تأكيد الطلب');
    body.appendChild(confirmBtn);

    var sheet = window.ErosUI.openSheet({
      title: t('checkout_title', 'إتمام الطلب'),
      content: body,
    });

    var delivery = giftCredit ? null : window.ErosCheckoutDelivery.mount(deliveryHost, function () {});
    var payment;

    // Wallet-due preview depends on the same shipping fee as the summary
    // (0 for pickup, else the selected delivery/gift rate) — re-mount
    // whenever either changes so the two never disagree, and so gift mode
    // re-filters the cod chip out of the rendered gateways (eros-checkout-
    // payment.js).
    function mountPayment() {
      var shippingIqd = shippingNow();
      // Remounts (fee refresh, birthday quote, fulfillment toggle) rebuild
      // the chips; carry the customer's choice across so a tap on «بطاقة»
      // made before the quote came back is not silently reset to COD
      // (audit 2026-09-13).
      var keep = payment && payment.getSelectedGatewayName ? payment.getSelectedGatewayName() : null;
      // A card in the cart: allowed chips only, and no wallet section.
      payment = hasCards
        ? window.ErosCheckoutPayment.mount(paymentHost, null, merchandiseNet(), shippingIqd, isGift, function () {}, keep, {
            allowedChipIds: giftConfig.paymentChipIds,
          })
        : window.ErosCheckoutPayment.mount(paymentHost, walletHost, merchandiseNet(), shippingIqd, isGift, function () {}, keep);
    }

    // The gift-credit order carries the customer's own phone and name in
    // place of an address (the card payment page needs the phone).
    var giftBuyerName = '';
    if (giftCredit) {
      window.ErosCommerce.getProfile()
        .then(function (res) {
          var p = (res && res.profile) || {};
          giftBuyerName = String(p.lastName || p.firstName || '').trim();
        })
        .catch(function () {});
    }

    // Ask the worker what this customer will actually pay. Every discount but
    // the personal offer (its own row) comes from here: the birthday code, a
    // cart code, an influencer link. A code the worker refuses prices at 0,
    // which is what confirm will charge.
    var quoteLines = window.ErosPromoRules.quoteLines(cart.items); // ADR-332 §2.5: only `_eros_gift` travels
    (hasCards ? Promise.resolve(null) : window.ErosCommerce.checkoutQuote(quoteLines, 'delivery', false, appliedDiscountCode, offerToken, currentAttribution()))
      .then(function (q) {
        promoQuote = q; // ADR-332 §4.5: the promotion rows, the banner and the delivery after promotions
        promoIqd = window.ErosPromoCartRender ? window.ErosPromoCartRender.sheet(q, summary, confirmBtn) : 0;
        refreshSummary();
        if (q && q.offer && offer.apply) offer.apply(q.offer);
        var quoted = quoteDiscountFor(q);
        if (quoted.iqd > 0) {
          quoteDiscountIqd = quoted.iqd;
          quoteDiscountKind = quoted.kind;
          refreshSummary();
          mountPayment();
        }
      })
      .catch(function () {});

    // isPickup is the «no shipping» branch of a cards-only gift-credit cart
    // (set once below, sent as 'pickup' on the wire); isGift is driven by the
    // altChip. They are never both true.
    function setFulfillment(pickup) {
      if (isPickup === pickup) return;
      isPickup = pickup;
      deliveryChip.setAttribute('aria-pressed', pickup ? 'false' : 'true');
      altChip.setAttribute('aria-pressed', pickup ? 'true' : 'false');
      deliveryHost.style.display = pickup ? 'none' : '';
      refreshSummary();
      mountPayment();
    }

    function setGiftMode(gift) {
      if (isGift === gift) return;
      isGift = gift;
      deliveryChip.setAttribute('aria-pressed', gift ? 'false' : 'true');
      altChip.setAttribute('aria-pressed', gift ? 'true' : 'false');
      refreshSummary();
      mountPayment();
    }

    // Gift credit rides the pickup branch: no address form, no delivery fee,
    // a single total row, fulfillmentMethod 'pickup' on the order.
    setFulfillment(giftCredit);
    deliveryChip.addEventListener('click', function () { setGiftMode(false); });
    altChip.addEventListener('click', function () { if (leftMode === 'gift') setGiftMode(true); });
    // Preset the fulfillment from the cart swipe/click that opened the sheet.
    if (initialMode === 'gift' && leftMode === 'gift') setGiftMode(true);

    // Live fee fetch — GET {commerceApiBaseUrl}/v1/checkout/shipping-fees,
    // same baseUrl()-driven request() wrapper every other ErosCommerce call
    // uses (eros-commerce-client.js). shippingFees already holds
    // FALLBACK_SHIPPING_FEES synchronously (set above, before the sheet
    // opens), so both success and failure paths just replace it in place
    // and repaint — no loading state needed, no undefined flash.
    (giftCredit ? Promise.reject(new Error('no_shipping')) : window.ErosCommerce.shippingFees())
      .then(function (res) {
        shippingFees = {
          delivery: {
            title: res.delivery.title,
            price: Number(res.delivery.price),
            currency: res.delivery.currency,
          },
          gift: { title: res.gift.title, price: Number(res.gift.price), currency: res.gift.currency },
        };
      })
      .catch(function () {
        shippingFees = FALLBACK_SHIPPING_FEES;
      })
      .then(function () {
        refreshSummary();
        mountPayment();
      });

    var addressAsked = false; // the customer answered the ADR-215 question with «نعم»
    confirmBtn.addEventListener('click', function () {
      error.textContent = '';
      // ADR-305: a card payment this browser opened is still open (its hold is ~10 min):
      // a second order for the cart waits for it (#199121061 was COD 16 s after a card payment).
      var pendingCard = window.ErosPendingPaymentBanner && window.ErosPendingPaymentBanner.current && window.ErosPendingPaymentBanner.current();
      if (pendingCard && Date.now() - pendingCard.startedAt < 15 * 60 * 1000) { // locked 15 min after the page opened
        error.textContent = t('card_payment_in_progress', 'الدفع قيد المعالجة — انتظر تأكيد الدفع بالبطاقة لهذه السلة قبل تثبيت طلب آخر.');
        return;
      }
      var savedAddressGid = null;
      var addressPayload = null;
      if (isPickup) {
        var session = window.ErosSession.get();
        addressPayload = { phone: session.e164, countryCode: 'IQ' };
        // One name → lastName, the account's own convention.
        if (giftCredit && giftBuyerName) addressPayload.lastName = giftBuyerName;
      } else {
        var deliveryState = delivery.getState();
        if (!deliveryState.valid) {
          // ADR-183: a saved address without a valid Alwaseet code is not
          // "incomplete details" — it needs updating, and the fix is one tap away.
          if (deliveryState.reason === 'address_needs_update') {
            error.textContent = t('address_needs_update', 'تعذّر تحديد هذا العنوان. حدّث المحافظة والمنطقة ثم أعد المحاولة.');
            if (delivery.openEditorForSelected) delivery.openEditorForSelected();
            return;
          }
          error.textContent = t('address_required', 'أكمل بيانات التوصيل');
          return;
        }
        addressPayload = deliveryState.address;
        savedAddressGid = deliveryState.savedAddressGid;
      }
      var gatewayName = payment.getSelectedGatewayName();
      if (!gatewayName) {
        error.textContent = t('payment_required', 'اختر طريقة دفع');
        return;
      }
      // ADR-215 §2.3: a governorate none of the saved addresses is in asks first.
      var addressQuestion = !isPickup && !addressAsked && window.ErosAddressCheck ? window.ErosAddressCheck.mismatch(delivery, deliveryState, isGift) : null;
      if (addressQuestion) {
        window.ErosAddressCheck.ask(addressQuestion, function () { addressAsked = true; confirmBtn.click(); });
        return;
      }
      addressAsked = false;

      var lines = window.ErosPromoRules.quoteLines(cart.items);

      // One event id per cart contents (2026-09-25): a second click after a
      // failed response used to mint a new id, and the worker — which only
      // stamped the id on the order — created a second order six seconds
      // after the first (#199120544/545). Same lines → same id, so the
      // worker's idempotency guard replays the order instead.
      var linesKey = JSON.stringify(lines);
      if (!confirmEventId || confirmEventKey !== linesKey) {
        confirmEventId = generateEventId();
        confirmEventKey = linesKey;
      }
      var payload = {
        lines: lines,
        paymentGatewayName: gatewayName,
        erosEventId: confirmEventId,
        fulfillmentMethod: isPickup ? 'pickup' : 'delivery',
        address: addressPayload,
      };
      if (savedAddressGid) payload.savedAddressGid = savedAddressGid;
      if (appliedDiscountCode) payload.discountCode = appliedDiscountCode;
      // ADR-213: the token as of this click (null once the countdown ended).
      var liveOfferToken = offerToken && window.ErosOffer ? window.ErosOffer.activeToken() : null;
      if (liveOfferToken) payload.offerToken = liveOfferToken;
      // ADR-225: the influencer link this browser arrived by ({ref, at}), only when there is one.
      var attribution = currentAttribution();
      if (attribution) payload.attribution = attribution;
      // ADR-289: the Venus conversation this browser arrived from (a cart link), id only.
      var assistant = window.ErosAssistantAttribution ? window.ErosAssistantAttribution.current() : null;
      if (assistant) payload.assistant = assistant;
      // ADR-187: this checkout never enters Shopify's, so the browser pixel
      // never fires Purchase — the worker's server Purchase is the only one.
      // Hand it the ad-click (_fbc) and browser (_fbp) ids so Meta can tie the
      // order to the ad; the worker stamps them on the order.
      var metaFbp = readCookie('_fbp');
      var metaFbc = readCookie('_fbc');
      if (metaFbp) payload.metaFbp = metaFbp;
      if (metaFbc) payload.metaFbc = metaFbc;
      // CheckoutConfirmInput.isGift: optional boolean, true only for gift —
      // omit the key entirely for delivery rather than sending false, per
      // the shared contract. Server-side handling (cod rejection, gift tag,
      // live shipping fee) already deployed; this is the only piece owed
      // from the web client.
      if (isGift) payload.isGift = true;
      // ADR-220 §6.1: opened «قد يعجبك» products still in the cart → `eros_reco`
      // on the order (reporting only).
      var recoRules = window.ErosRecoRules;
      if (recoRules) {
        try {
          var recoHits = recoRules.hitsForCheckout(
            JSON.parse(window.localStorage.getItem(recoRules.HITS_KEY) || '[]'),
            cart.items.map(function (item) { return recoRules.productGid(item.product_id); }),
            Math.floor(Date.now() / 1000)
          );
          if (recoHits.length) payload.recoHits = recoHits;
        } catch (e) {
          /* storage blocked: no attribute */
        }
      }

      confirmBtn.disabled = true;
      confirmBtn.textContent = t('confirming', 'جارٍ التأكيد...');

      // Audit finding 2026-08-09: open the payment tab NOW, synchronously,
      // while this click's user-activation is still live — confirmCheckout
      // below is a network round-trip (plus AlQaseh polling server-side),
      // and window.open() called after that resolves gets silently blocked
      // by popup blockers (always on Safari, after ~5s on Chrome). Closed
      // again below if this order didn't need a payment page after all.
      var paymentTab = window.ErosCheckoutPaymentRedirect.openBlankTab();

      // Owner 2026-09-25: the button must not hand itself back after a lost
      // response. On a network error it stays locked, says the order is
      // being verified, and retries once with the SAME event id after 3 s —
      // the worker replays the order if the first request had created it.
      var confirmAttempt = 0;
      function sendConfirm() {
        confirmAttempt += 1;
        return window.ErosCommerce.confirmCheckout(payload).catch(function (err) {
          if (err && err.code === 'network_error' && confirmAttempt < 2) {
            confirmBtn.textContent = t('confirm_verifying', 'جارٍ التحقق من الطلب...');
            return new Promise(function (resolve) { setTimeout(resolve, 3000); }).then(sendConfirm);
          }
          throw err;
        });
      }

      sendConfirm()
        .then(function (res) {
          sheet.close();
          // Deferred AlQaseh checkout (res.checkoutId, no order yet): the
          // cart is cleared only once payment is actually confirmed — see
          // eros-checkout-payment-redirect.js's checkOnce(), which clears it
          // the moment polling reports paid:true. Clearing here would leave
          // a failed/abandoned card payment with an empty cart and nothing
          // to retry into visually; every other gateway keeps clearing
          // immediately since there's no external redirect step to fail.
          if (!res.checkoutId) clearCart();

          if (res.discountApplied === false) {
            window.ErosUI.toast(
              t('discount_code_rejected', 'تعذّر تطبيق كود الخصم — تم تأكيد الطلب بالسعر الكامل'),
              'error',
            );
          }

          if (res.paymentPageUrl) {
            // res.checkoutId (new card checkout, no order yet) vs
            // res.orderGid/orderName (existing order switching to card,
            // unaffected by the deferred-order flow) — see
            // ErosCheckoutPaymentRedirect.open's ids param.
            var ids = res.checkoutId
              ? { checkoutId: res.checkoutId }
              : { orderGid: res.orderGid, orderName: res.orderName };
            window.ErosCheckoutPaymentRedirect.open(
              res.paymentPageUrl,
              ids,
              paymentTab,
              res.reservedUntil,
            );
          } else {
            if (paymentTab && !paymentTab.closed) paymentTab.close();
            confirmEventId = null;
            confirmEventKey = null;
            window.ErosUI.toast(t('order_placed', 'تم استلام طلبك بنجاح') + ' — ' + res.orderName, 'success');
            // Web push intent moment (ADR-172): the order exists — offer the
            // delivery-updates opt-in now, never on landing.
            try { document.dispatchEvent(new CustomEvent('eros:web-push-moment', { detail: { source: 'order_placed' } })); } catch (e) { /* ignore */ }
          }
        })
        .catch(function (err) {
          if (paymentTab && !paymentTab.closed) paymentTab.close();
          if (window.ErosPromoCart && window.ErosPromoCart.termsChanged(err, function () { sheet.close(); proceedToCheckout(initialMode); })) return; // ADR-332 §4.5
          error.textContent = mapConfirmError(err);
          var blocked = window.ErosCommerce.customerBlocked(err); // ADR-301
          if (blocked && blocked.contactUrl) {
            error.appendChild(document.createElement('br'));
            error.appendChild(window.ErosCommerce.customerBlockedLink(blocked.contactUrl, t('contact_us', 'تواصل معنا')));
          }
          confirmBtn.disabled = false;
          confirmBtn.textContent = t('confirm', 'تأكيد الطلب');
          // ADR-183: the worker is the authority — if it refused an address
          // this page believed valid, open the editor on that saved row.
          if (err && err.code === 'address_needs_update' && delivery && delivery.openEditorFor) {
            delivery.openEditorFor(err.details && err.details.savedAddressGid);
          }
        });
    });
  }

  function mapConfirmError(err) {
    if (err.code === 'card_checkout_in_progress') return t('card_payment_in_progress', 'الدفع قيد المعالجة — انتظر تأكيد الدفع بالبطاقة لهذه السلة قبل تثبيت طلب آخر.'); // ADR-305
    var blocked = window.ErosCommerce.customerBlocked(err);
    if (blocked) return blocked.message;
    if (err.code === 'lines_required') return t('empty_cart', 'السلة فاضية');
    if (err.code === 'address_required' || err.code === 'phone_required' || err.code === 'address1_required' || err.code === 'city_required') {
      return t('address_required', 'أكمل بيانات التوصيل');
    }
    if (err.code === 'payment_gateway_required') return t('payment_required', 'اختر طريقة دفع');
    if (err.code === 'address_needs_update') {
      return t('address_needs_update', 'تعذّر تحديد هذا العنوان. حدّث المحافظة والمنطقة ثم أعد المحاولة.');
    }
    // Card checkout refused by commerce-worker's confirm-time stock gate
    // (2026-09-06) — an item sold out between opening the cart and pressing
    // pay. `details.unavailable` names the lines, so tell the customer WHICH
    // item rather than a generic failure.
    if (err.code === 'out_of_stock') {
      var names = ((err.details && err.details.unavailable) || [])
        .map(function (item) { return item && item.title; })
        .filter(Boolean);
      if (names.length) {
        return t('out_of_stock_items', 'نفدت الكمية من:') + ' ' + names.join('، ');
      }
      return t('out_of_stock', 'نفدت كمية أحد المنتجات في سلتك. راجع السلة ثم أعد المحاولة.');
    }
    // Credit covers the whole order, so there is nothing to put on a card.
    if (err.code === 'wallet_covers_total') {
      return t('wallet_covers_total', 'رصيدك يغطي هذا الطلب بالكامل — اختر طريقة دفع أخرى.');
    }
    // ADR-200: the server's gift-credit checkout refusals.
    var giftMessage = window.ErosGiftCredit && window.ErosGiftCredit.checkoutErrorMessage(err.code);
    if (giftMessage) return giftMessage;
    return t('generic_error', 'حدث خطأ، حاول مرة أخرى');
  }

  function clearCart() {
    // The drawer answers cart:refresh by re-fetching its sections. Fired
    // before /cart/clear.js had completed, it redrew the old lines and the
    // customer saw a full cart until reload — a second confirm then said
    // «السلة فاضية» (owner 2026-09-25). The refresh now waits for the clear
    // to settle, success or failure.
    return fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .catch(function () {})
      .then(function () {
        // ADR-213: the order exists — the offer is spent, forget its token.
        if (window.ErosOffer) window.ErosOffer.clear();
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      });
  }

  function bindTriggers() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest ? e.target.closest('[data-eros-checkout-trigger]') : null;
      if (!trigger) return;
      e.preventDefault();
      openCheckout();
    });
  }

  bindTriggers();

  // ADR-331 W0: the discount the quote puts on the order, outside the
  // personal offer (which has its own row and its own amount). Pure.
  function quoteDiscountFor(q) {
    if (!q || !q.discountKind || q.discountKind === 'offer') return { iqd: 0, kind: null };
    var iqd = Math.round(Number(q.discountIqd));
    return Number.isFinite(iqd) && iqd > 0 ? { iqd: iqd, kind: q.discountKind } : { iqd: 0, kind: null };
  }

  // Shared fulfillment config so the cart swipe bar renders the exact same
  // left-slot option the checkout sheet will honor (gift > none).
  function fulfillmentLeftMode() {
    var s = (window.themeVariables || {}).settings || {};
    if (s.giftSendEnabled) return 'gift';
    return 'none';
  }

  window.ErosCheckout = { open: openCheckout, leftMode: fulfillmentLeftMode, quoteDiscountFor: quoteDiscountFor };
})();
